import React from 'react';

import CheckIcon from '@mui/icons-material/Check';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';

import { CHECKLIST_STATUS_STYLES } from '../constants';
import type { ManualApprovalState } from '../types';
import type { EgsCapacityChecklistRow } from '../utils';
import { formatOptionalNumber, isFiniteNumber } from '../utils';

import ChecklistStatusDot from './ChecklistStatusDot';
import EmptyTableRow from './EmptyTableRow';
import HiddenApprovedFooter from './HiddenApprovedFooter';
import NumberField from './NumberField';
import ReadOnlyField from './ReadOnlyField';
import ReportCard from './ReportCard';
import ReportNote from './ReportNote';
import SortTableCell from './SortTableCell';

interface Props {
  hiddenApprovedCount: number;
  manualApprovals: ManualApprovalState;
  rows: Array<EgsCapacityChecklistRow>;
  showAutoApproved: boolean;
  onApprovalChange: (rowId: string) => void;
  onValueChange: (
    rowId: string,
    fieldName: 'egsCapacity' | 'includedCapacity' | 'maxRoomCapacity',
    value?: number,
  ) => void;
}

export default function CapacityReportCard({
  hiddenApprovedCount,
  manualApprovals,
  rows,
  showAutoApproved,
  onApprovalChange,
  onValueChange,
}: Props) {
  return (
    <ReportCard>
      <ReportNote
        icon={<InfoOutlinedIcon />}
        text={
          <>
            <strong>EGS capacity check:</strong> auto-approve when{' '}
            <strong>Max Room Capacity = Included Capacity + EGS Capacity</strong>. Use <strong>Approve</strong> to
            manually approve remaining capacity rows.
          </>
        }
      />
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1180 }}>
          <TableHead>
            <TableRow>
              <SortTableCell>Deal Option / Room Asset</SortTableCell>
              <SortTableCell>Included Capacity</SortTableCell>
              <SortTableCell>Max Room Capacity</SortTableCell>
              <SortTableCell>EGS Contracted For</SortTableCell>
              <SortTableCell>Extra Guests</SortTableCell>
              <SortTableCell>Included + EGS</SortTableCell>
              <SortTableCell>Capacity Check</SortTableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 ? (
              <EmptyTableRow colSpan={8} label="No EGS capacity rows match this view." />
            ) : (
              rows.map((row) => {
                const manuallyApproved = !!manualApprovals[row.id];
                const approved = row.status === 'approved' || manuallyApproved;

                return (
                  <TableRow key={row.id} hover sx={approved ? CHECKLIST_STATUS_STYLES.approvedRow : undefined}>
                    <TableCell sx={{ minWidth: 300 }}>
                      <ReadOnlyField value={row.name} />
                      <ReadOnlyField subdued value={row.roomName} />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        ariaLabel={`${row.name} included capacity`}
                        value={row.includedCapacity}
                        onChange={(value) => onValueChange(row.id, 'includedCapacity', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        ariaLabel={`${row.name} max room capacity`}
                        value={row.maxRoomCapacity}
                        onChange={(value) => onValueChange(row.id, 'maxRoomCapacity', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <NumberField
                        ariaLabel={`${row.name} EGS contracted for`}
                        value={row.egsCapacity}
                        onChange={(value) => onValueChange(row.id, 'egsCapacity', value)}
                      />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField value={formatOptionalNumber(row.egsCapacity)} />
                    </TableCell>
                    <TableCell>
                      <ReadOnlyField
                        value={
                          isFiniteNumber(row.includedCapacity) && isFiniteNumber(row.egsCapacity)
                            ? String(row.includedCapacity + row.egsCapacity)
                            : 'N/A'
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <ChecklistStatusDot
                        approvalType={manuallyApproved ? 'manual' : 'auto'}
                        status={approved ? 'approved' : row.status}
                        band={approved ? 'green' : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      {!approved && (
                        <Button
                          size="small"
                          variant="contained"
                          startIcon={<CheckIcon />}
                          onClick={() => onApprovalChange(row.id)}
                        >
                          Approve
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <HiddenApprovedFooter count={hiddenApprovedCount} noun="EGS capacity row" showAutoApproved={showAutoApproved} />
    </ReportCard>
  );
}
